"use client"

import { useState, useEffect } from "react"
import { Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom"
import {
  FaStoreAlt,
  FaChartLine,
  FaLayerGroup,
  FaUsers,
  FaSignOutAlt,
  FaFileImport,
  FaSignInAlt,
  FaEnvelope,
  FaInfoCircle,
  FaBook,
  FaStore,
  FaUser,
  FaCog,
  FaLightbulb,
} from "react-icons/fa"
import {
  Flex,
  Box,
  Text,
  Button,
  Image,
  useTheme,
  Link,
  Divider,
  IconButton,
  Tooltip,
  useColorModeValue,
  Badge,
} from "@chakra-ui/react"
import { Helmet } from "react-helmet"

import Users from "./pages/admin/users/Users"
import AddUser from "./pages/admin/users/AddUser"
import EditPageAccess from "./pages/admin/users/EditPageAccess"
import EditSupplierAccess from "./pages/admin/users/EditSupplierAccess"
import EditRetailerAccess from "./pages/admin/users/EditRetailerAccess"

import Articles from "./pages/admin/articles/Articles"
import Stores from "./pages/admin/stores/Stores"
import LoginHistory from "./pages/admin/history/LoginHistory"

import Sales from "./pages/sales/Sales"

import Imports from "./pages/admin/imports/Imports"

import Edi from "./pages/admin/edi/Edi"
import PnpFtp from "./pages/admin/pnpFtp/PnpFtp"

import Delete from "./pages/admin/delete/Delete"
import Validation from "./pages/admin/validation/Validation"
import BatchReports from "./pages/admin/batchReports/BatchReports"

import StrikeRate from "./pages/strike/StrikeRate"

import Ranging from "./pages/ranging/Ranging"

import Margin from "./pages/margin/Margin"

import Signin from "./pages/login/Signin"
import SignupPage from "./pages/login/Signup"
import Contact from "./pages/admin/marketing/Contact"
import About from "./pages/admin/marketing/About"

import MyProfile from "./pages/myProfile/MyProfile"
import Insights from "./pages/insights/Insights"
import Orders from "./pages/orders/Orders"

import NotificationPopup from "./components/NotificationPopup"
import TrialBanner from "./components/TrialBanner"

import { useAuthentication } from "./wrappers/UseAuthentication"
import RetailerNavigation from "./components/RetailerNavigation"
import SupplierNavigation from "./components/SupplierNavigation"
import SupplierLogo from "./components/SupplierLogo"
import { useSupplierRetailer } from "./wrappers/SupplierRetailerContext"
import { usePageAccess } from "./wrappers/PageAccessContext"

import TermsConditions from "./pages/legal/TermsConditions"
import PrivacyPolicy from "./pages/legal/PrivacyPolicy"
import Footer from "./components/Footer"

const Layout = () => {
  const { isAuthenticated, signOut } = useAuthentication()
  const { retailers, user_supplier_access, loading } = useSupplierRetailer()
  const {
    user_page_access,
    loading: pageLoading,
    restrictedyn,
    subscriptionyn,
    trialyn,
    paymentReminder,
  } = usePageAccess()
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [showMigrationNotice, setShowMigrationNotice] = useState(true)
  const theme = useTheme()
  const location = useLocation()

  // Enhanced styling for navigation
  const sidebarBg = useColorModeValue(
    "url('/navbar_bg.png')",
    "url('/navbar_bg.png')",
  )

  const getLinkStyles = (path) => {
    const isActive = location.pathname === path

    const base = {
      width: "100%",
      textDecoration: "none",
      color: "white",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      borderRadius: "12px",
      mb: "3px",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      "& svg": {
        transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
    }

    if (isActive) {
      return {
        ...base,
        fontWeight: "bold",
        bg: "rgba(255, 255, 255, 0.22)",
        border: "1px solid rgba(255, 255, 255, 0.35)",
        borderLeft: `3px solid ${theme.colors.secondary1 || "#58c087"}`,
        boxShadow:
          "0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.3), inset 0 -1px 1px rgba(255, 255, 255, 0.1)",
        _hover: {
          bg: "rgba(255, 255, 255, 0.28)",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          borderLeft: `3px solid ${theme.colors.secondary1 || "#58c087"}`,
          boxShadow:
            "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.35), inset 0 -1px 1px rgba(255, 255, 255, 0.1)",
          "& svg": { transform: "scale(1.15)" },
        },
      }
    }

    return {
      ...base,
      fontWeight: "normal",
      bg: "rgba(255, 255, 255, 0.06)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
      _hover: {
        bg: "rgba(255, 255, 255, 0.15)",
        border: "1px solid rgba(255, 255, 255, 0.25)",
        transform: "translateX(4px)",
        boxShadow:
          "0 6px 24px rgba(0, 0, 0, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05)",
        "& svg": { transform: "scale(1.2) rotate(-8deg)" },
      },
    }
  }

  const [adminImage, setAdminImage] = useState("/spearbi_ai1.jpg")
  useEffect(() => {
    const userid = localStorage.getItem("userid")
    if (userid == 5 || userid == "5") {
      setAdminImage("/DAWN3.jpg")
    }
  }, [])

  useEffect(() => {
    if (paymentReminder) {
      setIsNotificationOpen(true)
    }
  }, [paymentReminder])

  // Group navigation items for better organization
  const renderNavGroup = (title, items) => {
    if (items.length === 0) return null

    return (
      <Box mb={4}>
        {title && (
          <Text fontSize="xs" color="gray.300" textTransform="uppercase" mb={2} px={4} fontWeight="bold">
            {title}
          </Text>
        )}
        {items}
      </Box>
    )
  }

  // Create navigation items based on user access
  const getAnalyticsNavItems = () => {
    const items = []

    const userid = localStorage.getItem("userid")
    const isAdmin = userid === "1" || userid === "2"

    if (user_page_access.some((page) => page.value === "Sales" && !restrictedyn)) {
      items.push(
        <Link key="sales" as={NavLink} to="/sales" sx={getLinkStyles("/sales")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaStoreAlt />
            <Text ml={3}>Sales</Text>
          </Box>
        </Link>,
      )
    }

    // Insights — admin only (userid 1 or 2). Placed directly under Sales.
    if (isAdmin) {
      items.push(
        <Link key="insights" as={NavLink} to="/insights" sx={getLinkStyles("/insights")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaLightbulb />
            <Text ml={3}>Insights</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "StrikeRate" && !restrictedyn)) {
      items.push(
        <Link key="strike" as={NavLink} to="/strike" sx={getLinkStyles("/strike")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaChartLine />
            <Text ml={3}>Strike Rate</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Ranging" && !restrictedyn)) {
      items.push(
        <Link key="ranging" as={NavLink} to="/ranging" sx={getLinkStyles("/ranging")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaLayerGroup />
            <Text ml={3}>Ranging</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Margin" && !restrictedyn)) {
      items.push(
        <Link key="margin" as={NavLink} to="/margin" sx={getLinkStyles("/margin")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaStoreAlt />
            <Text ml={3}>Margin</Text>
          </Box>
        </Link>,
      )
    }

    // Orders — admin only (userid 1 or 2)
    if (isAdmin) {
      items.push(
        <Link key="orders" as={NavLink} to="/orders" sx={getLinkStyles("/orders")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>Orders</Text>
          </Box>
        </Link>,
      )
    }

    return items
  }

  const getAdminNavItems = () => {
    const items = []

    if (user_page_access.some((page) => page.value === "Users")) {
      items.push(
        <Link key="users" as={NavLink} to="/users" sx={getLinkStyles("/users")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaUsers />
            <Text ml={3}>Users</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Articles")) {
      items.push(
        <Link key="articles" as={NavLink} to="/articles" sx={getLinkStyles("/articles")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaBook />
            <Text ml={3}>Articles</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Stores")) {
      items.push(
        <Link key="stores" as={NavLink} to="/stores" sx={getLinkStyles("/stores")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaStore />
            <Text ml={3}>Stores</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Imports")) {
      items.push(
        <Link key="imports" as={NavLink} to="/imports" sx={getLinkStyles("/imports")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>Imports</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Edi")) {
      items.push(
        <Link key="edi" as={NavLink} to="/edi" sx={getLinkStyles("/edi")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>Edi</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "Delete")) {
      items.push(
        <Link key="delete" as={NavLink} to="/delete" sx={getLinkStyles("/delete")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>Delete</Text>
          </Box>
        </Link>,
      )
    }

    const userid = localStorage.getItem("userid")
    if (userid === "1" || userid === "2") {
      items.push(
        <Link key="pnp-ftp" as={NavLink} to="/pnp-ftp" sx={getLinkStyles("/pnp-ftp")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>PNP FTP</Text>
          </Box>
        </Link>,
      )
      items.push(
        <Link key="validation" as={NavLink} to="/validation" sx={getLinkStyles("/validation")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaCog />
            <Text ml={3}>Validation</Text>
          </Box>
        </Link>,
      )
      items.push(
        <Link key="batch-reports" as={NavLink} to="/batch-reports" sx={getLinkStyles("/batch-reports")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaFileImport />
            <Text ml={3}>Reports</Text>
          </Box>
        </Link>,
      )
    }

    return items
  }

  const getOtherNavItems = () => {
    const items = []

    if (user_page_access.some((page) => page.value === "MyProfile")) {
      items.push(
        <Link key="myProfile" as={NavLink} to="/myProfile" sx={getLinkStyles("/myProfile")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaUser />
            <Text ml={3}>My Profile</Text>
          </Box>
        </Link>,
      )
    }

    if (user_page_access.some((page) => page.value === "LoginHistory")) {
      items.push(
        <Link key="loginhistory" as={NavLink} to="/loginhistory" sx={getLinkStyles("/loginhistory")}>
          <Box display="flex" alignItems="center" py={2} px={4}>
            <FaUsers />
            <Text ml={3}>History</Text>
          </Box>
        </Link>,
      )
    }

    return items
  }

  const getPublicNavItems = () => {
    const items = []

    items.push(
      <Link key="signin" as={NavLink} to="/signin" sx={getLinkStyles("/signin")}>
        <Box display="flex" alignItems="center" py={2} px={4}>
          <FaSignInAlt />
          <Text ml={3}>Sign In</Text>
        </Box>
      </Link>,
    )

    items.push(
      <Link key="contact" as={NavLink} to="/contact-us" sx={getLinkStyles("/contact-us")}>
        <Box display="flex" alignItems="center" py={2} px={4}>
          <FaEnvelope />
          <Text ml={3}>Contact Us</Text>
        </Box>
      </Link>,
    )

    items.push(
      <Link key="about" as={NavLink} to="/about-us" sx={getLinkStyles("/about-us")}>
        <Box display="flex" alignItems="center" py={2} px={4}>
          <FaInfoCircle />
          <Text ml={3}>About Us</Text>
        </Box>
      </Link>,
    )

    items.push(
      <Link key="signup" as={NavLink} to="/signup" sx={getLinkStyles("/signup")}>
        <Box display="flex" alignItems="center" py={2} px={4} mt={2}>
          <FaSignInAlt />
          <Text ml={3}>SME SignUp</Text>
          <Badge ml={2} colorScheme="green" fontSize="xs">
            New
          </Badge>
        </Box>
      </Link>,
    )

    return items
  }

  return (
    <Flex minHeight="100vh" overflow="hidden">
      <NotificationPopup
        key={paymentReminder}
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        message={paymentReminder}
      />

      <TrialBanner trialyn={trialyn} />

      {/* Enhanced Sidebar */}
      <Flex
        as="nav"
        direction="column"
        bgImage="url('/navbar_bg.png')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        color="white"
        width="200px"
        height="100vh"
        position="fixed"
        boxShadow="0 0 20px rgba(0, 0, 0, 0.1)"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "24px",
          },
        }}
      >
        {/* Logo Container */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="111.73px"
          bg="white"
          borderBottom="1px solid rgba(0, 0, 0, 0.1)"
          position="relative"
          overflow="hidden"
        >
          <Image
            src={adminImage || "/placeholder.svg"}
            alt="Logo"
            objectFit="cover"
            transition="transform 0.3s ease"
            _hover={{ transform: "scale(1.05)" }}
          />
        </Box>

        {/* Profile Settings Button (when authenticated) */}
        {isAuthenticated &&
          !pageLoading &&
          user_page_access.length > 0 &&
          user_page_access.some((page) => page.value === "MyProfile") && (
            <Tooltip label="My Profile" placement="left">
              <IconButton
                as={NavLink}
                to="/myProfile"
                icon={<FaCog color="white" />}
                aria-label="My Profile"
                variant="ghost"
                size="md"
                fontSize="xl"
                position="fixed"
                top="15px"
                right="105px"
                zIndex="9999"
                bg={`${theme.colors.primary1}`}
                p="8px"
                borderRadius="50%"
                boxShadow="0px 0px 10px rgba(0, 0, 0, 0.2)"
                _hover={{
                  transform: "rotate(30deg)",
                  bg: theme.colors.secondary1,
                  color: "black",
                }}
                transition="all 0.3s ease"
              />
            </Tooltip>
          )}

        {/* Navigation Items Container */}
        <Box flex="1" py={4} px={2}>
          {/* Supplier Navigation (when authenticated) */}
          {isAuthenticated && !pageLoading && user_page_access.length > 0 && (
            <>
              {user_supplier_access.length > 0 && (
                <Box mb={4}>
                  <SupplierNavigation />
                </Box>
              )}

              {/* Analytics Navigation Group */}
              {renderNavGroup("Analytics", getAnalyticsNavItems())}

              {/* Admin Navigation Group */}
              {getAdminNavItems().length > 0 && (
                <>
                  <Divider my={4} borderColor="rgba(255, 255, 255, 0.2)" />
                  {renderNavGroup("Administration", getAdminNavItems())}
                </>
              )}

              {/* Other Navigation Group */}
              {getOtherNavItems().length > 0 && (
                <>
                  <Divider my={4} borderColor="rgba(255, 255, 255, 0.2)" />
                  {renderNavGroup("Account", getOtherNavItems())}
                </>
              )}
            </>
          )}

          {/* Public Navigation (when not authenticated) */}
          {!isAuthenticated && <>{renderNavGroup("", getPublicNavItems())}</>}
        </Box>

        {/* Logout Button (when authenticated) */}
        {isAuthenticated && (
          <Box p={4} borderTop="1px solid rgba(255, 255, 255, 0.1)">
            <Button
              leftIcon={<FaSignOutAlt />}
              onClick={signOut}
              variant="outline"
              width="100%"
              bg="rgba(255, 255, 255, 0.06)"
              borderColor="rgba(255, 255, 255, 0.15)"
              borderRadius="10px"
              backdropFilter="blur(12px)"
              WebkitBackdropFilter="blur(12px)"
              boxShadow="0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)"
              _hover={{
                bg: "rgba(255, 255, 255, 0.15)",
                borderColor: "rgba(255, 255, 255, 0.25)",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
                "& svg": {
                  transform: "translateX(-3px)",
                },
              }}
              _active={{
                transform: "scale(0.96)",
                boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
              color="white"
              transition="all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
              sx={{
                "& svg": {
                  transition: "transform 0.3s ease",
                },
              }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Flex>

      {/* Main Content Area */}
      <Flex
        direction="column"
        flex="1"
        overflow="auto"
        ml="200px"
        bg={useColorModeValue("gray.50", "gray.900")}
        minHeight="100vh"
      >
        {/* Supplier Logo */}
        <SupplierLogo />

        {/* Content Container */}
        <Box px={2} py={6} flex="1">
          {/* Retailer Navigation */}
          {isAuthenticated && !loading && retailers.length > 0 && (
            <Box mb={6} bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <RetailerNavigation />
            </Box>
          )}

          {/* Routes */}
          <Routes>
            <Route path="/signin" element={<Signin />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/about-us" element={<About />} />
            {isAuthenticated && (
              <>
                <Route
                  path="/myProfile"
                  element={
                    <>
                      <Helmet>
                        <title>MyProfile - Spearbi</title>
                        <meta name="description" content="Edit & manage your profile & subscription" />
                      </Helmet>
                      <MyProfile />
                    </>
                  }
                />
                <Route
                  path="/sales"
                  element={
                    <>
                      <Helmet>
                        <title>Sales - Spearbi</title>
                        <meta
                          name="description"
                          content="Analyze your sales data comprehensively with Spearbi's reporting tools."
                        />
                      </Helmet>
                      <Sales />
                    </>
                  }
                />
                <Route
                  path="/strike"
                  element={
                    <>
                      <Helmet>
                        <title>Strike Rate - Spearbi</title>
                        <meta
                          name="description"
                          content="Evaluate and improve your strike rate with Spearbi's advanced analytics."
                        />
                      </Helmet>
                      <StrikeRate />
                    </>
                  }
                />
                <Route
                  path="/ranging"
                  element={
                    <>
                      <Helmet>
                        <title>Ranging - Spearbi</title>
                        <meta
                          name="description"
                          content="Identify active articles in stores across selected retailers with Spearbi's reporting tools."
                        />
                      </Helmet>
                      <Ranging />
                    </>
                  }
                />
                <Route
                  path="/margin"
                  element={
                    <>
                      <Helmet>
                        <title>Margin Report - Spearbi</title>
                        <meta
                          name="description"
                          content="Analyze your margin report comprehensively with Spearbi's reporting tools."
                        />
                      </Helmet>
                      <Margin />
                    </>
                  }
                />
                <Route
                  path="/insights"
                  element={
                    <>
                      <Helmet>
                        <title>Insights - Spearbi</title>
                        <meta
                          name="description"
                          content="Advanced insights and analytics for Spearbi administrators."
                        />
                      </Helmet>
                      <Insights />
                    </>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <>
                      <Helmet>
                        <title>Orders - Spearbi</title>
                        <meta
                          name="description"
                          content="PNP Orders management for Spearbi administrators."
                        />
                      </Helmet>
                      <Orders />
                    </>
                  }
                />
                <Route
                  path="/imports"
                  element={
                    <>
                      <Helmet>
                        <title>Imports - Spearbi</title>
                        <meta
                          name="description"
                          content="Manage and analyze your imports with Spearbi's robust reporting features."
                        />
                      </Helmet>
                      <Imports />
                    </>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <>
                      <Helmet>
                        <title>Users - Spearbi</title>
                        <meta
                          name="description"
                          content="Manage user accounts and permissions within the Spearbi system."
                        />
                      </Helmet>
                      <Users />
                    </>
                  }
                />
                <Route
                  path="/articles"
                  element={
                    <>
                      <Helmet>
                        <title>Articles - Spearbi</title>
                        <meta
                          name="description"
                          content="Manage supplier articles and permissions within the Spearbi system."
                        />
                      </Helmet>
                      <Articles />
                    </>
                  }
                />
                <Route
                  path="/stores"
                  element={
                    <>
                      <Helmet>
                        <title>Stores - Spearbi</title>
                        <meta
                          name="description"
                          content="Manage retailer stores and permissions within the Spearbi system."
                        />
                      </Helmet>
                      <Stores />
                    </>
                  }
                />
                <Route
                  path="/loginhistory"
                  element={
                    <>
                      <Helmet>
                        <title>Login History - Spearbi</title>
                        <meta
                          name="description"
                          content="Admin functionality to view login history within the Spearbi system."
                        />
                      </Helmet>
                      <LoginHistory />
                    </>
                  }
                />
                <Route
                  path="/edi"
                  element={
                    <>
                      <Helmet>
                        <title>EDI - Spearbi</title>
                        <meta
                          name="description"
                          content="Manage Edi functionality for all Spear bi's selected retailers"
                        />
                      </Helmet>
                      <Edi />
                    </>
                  }
                />
                <Route
                  path="/delete"
                  element={
                    <>
                      <Helmet>
                        <title>EDI - Spearbi</title>
                        <meta name="description" content="Delete & manage data within Spearbi's system" />
                      </Helmet>
                      <Delete />
                    </>
                  }
                />
                <Route
                  path="/pnp-ftp"
                  element={
                    <>
                      <Helmet>
                        <title>PNP FTP - Spearbi</title>
                        <meta name="description" content="Monitor PNP EDI file processing and FTP status" />
                      </Helmet>
                      <PnpFtp />
                    </>
                  }
                />
                <Route
                  path="/validation"
                  element={
                    <>
                      <Helmet>
                        <title>Validation - Spearbi</title>
                        <meta name="description" content="Data validation tools" />
                      </Helmet>
                      <Validation />
                    </>
                  }
                />
                <Route
                  path="/batch-reports"
                  element={
                    <>
                      <Helmet>
                        <title>Batch Reports - Spearbi</title>
                        <meta name="description" content="Generate and email batch sales tracker reports" />
                      </Helmet>
                      <BatchReports />
                    </>
                  }
                />

                <Route path="/addUser" element={<AddUser />} />
                <Route path="/editPageAccess/:userid" element={<EditPageAccess />} />
                <Route path="/editSupplierAccess/:userid" element={<EditSupplierAccess />} />
                <Route path="/editRetailerAccess/:userid" element={<EditRetailerAccess />} />
              </>
            )}
                        <Route
              path="/terms"
              element={
                <>
                  <Helmet>
                    <title>Terms & Conditions - SpearBI</title>
                    <meta name="description" content="Terms and conditions for using the SpearBI Reporting System" />
                  </Helmet>
                  <TermsConditions />
                </>
              }
            />
            <Route
              path="/privacy"
              element={
                <>
                  <Helmet>
                    <title>Privacy Policy - SpearBI</title>
                    <meta name="description" content="Privacy policy for the SpearBI Reporting System" />
                  </Helmet>
                  <PrivacyPolicy />
                </>
              }
            />
            <Route path="*" element={<Navigate to="/signin" replace />} />
            <Route path="/" element={<Signin />} />
          </Routes>
        </Box>
        <Footer />
      </Flex>
    </Flex>
  )
}

export default Layout
